import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {useTranslate} from "@tolgee/react";
import Box from "@material-ui/core/Box";
import {useDispatch, useSelector} from "react-redux";
import {setShowCartCount} from "store/badges/slice";
import {groupBy, isEqual} from "lodash";
import {useCurrentShow, useCurrentShowId, useShowProductsByIds} from "hooks";
import {
  goToCart,
  goToPayment,
  selectCartStepperStatus,
  updateTotalAmount,
} from "store/exhibitors/cartStepper/slice";
import ReviewIcon from "view/images/ReviewIcon";
import PaymentIcon from "@material-ui/icons/Payment";
import Stepper, {StepperImageContainer} from "view/components/Stepper";
import {
  CartProgress,
  CartWithApprovalAmount,
} from "store/exhibitors/cartStepper/helper";
import {validate as isValidUuid} from "uuid";
import {getItem, writeItem} from "tools/localStorage";
import {
  ExtraProduct,
  FullProduct,
  LocalStorageExtraProduct,
  LocalStorageProduct,
  ProductNoteAttachment,
  QuickOrderProductType,
  RawNotes,
  ShowProductConfig,
} from "ts/api/product";
import {updateNotesAttachments} from "api/order";
import ShoppingCart from "view/components/ShoppingCart";
import ShoppingSummary from "view/components/ShoppingSummary";
import {getProductsTotalQuantity} from "view/components/ShoppingPage/helpers";
import {PaymentMethodType} from "ts/api/paymentMethod";
import separateProductsWithApproval from "tools/product/separateProductsWithApproval";
import {getLinkedPropertyByProductId} from "tools/product/productLinkedProperties";
import {ShoppingSummaryContextProvider} from "view/components/ShoppingSummary/context";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import useCurrentExhibitorPair from "hooks/useCurrentExhibitorPair";
import {generateTestId} from "tools/cypress/generateTestId";
import CartDiscountCodes from "view/apps/Exhibitor/pages/Cart/components/DiscountCodes";
import FlexBox from "view/components/Layout/FlexBox";
import {
  useShowDiscountCodes,
  useShowDiscountCodesActions,
} from "store/discount-code/discount-code-store";
import {AmountsState} from "view/apps/Exhibitor/pages/Cart/types";
import {MIN_AMOUNT_BY_CURRENCY} from "view/apps/Exhibitor/pages/Cart/components/Payment/shared";
import orderRepository from "api/shows/orders";
import {useQuickOrderId} from "hooks/show/order";
import {getUniqueProductCategoryIdsBySupplier} from "tools/product/getUniqueProductCategoryIdsBySupplier";
import roundCartQuantity from "tools/product/roundCartQuantity";
import {apiV2} from "api/firebase";
import {
  calculatePaymentFeeAmount,
  calculatePlatformFeeAmount,
  calculateProductPriceWithFees,
} from "hooks/helpers";
import {
  CommonNotificationType,
  ICustomProductOptionPrice,
  INotificationBaseMessage,
  INotificationBaseRelatedToShow,
  IPayloadUpdatedSurcharge,
} from "@evennt-tech/api-v2-core";
import {IdleManager} from "tools/idle";
import {useProductPurchasesPerStandActions} from "store/product/product-purchase-per-stand.store";
import {getMergedProjectCode} from "tools/projectCode";
import {isBankTransferAllowedByOrder} from "tools/order/isBankTransferAvailable";
import {useCategoryCodesState} from "store/categoryCode/category-code.store";
import {ICustomProductOptionConfig} from "@evennt-tech/api-v2-core/dist/custom-product-option";
import {useCartLocalStorageKey} from "./shared";
import Checkout from "./components/Checkout";
import {useResumeFailedIdealPayment} from "./helpers/useResumeFailedIdealPayment";
import {
  applyDiscountCodesToProducts,
  getAmounts,
  getFinalTotalAmount,
  mapProductToLocalStorageProduct,
  mergeProducts as mergeProductsHelper,
} from "./helpers";
import {useStyles} from "./style";

type ChangingProductFields = {
  cartQuantity?: number;
  notes?: {
    note?: string;
    attachments?: ProductNoteAttachment[];
  };
};

const initialAmountsState: AmountsState = {
  approvalNotRequired: {
    totalAmount: 0,
    subtotalAmount: 0,
    taxAmount: 0,
    paymentFeeAmount: 0,
    discountAmount: 0,
    productsAmount: 0,
  },
  approvalIsRequired: {
    totalAmount: 0,
    subtotalAmount: 0,
    taxAmount: 0,
    paymentFeeAmount: 0,
    discountAmount: 0,
    productsAmount: 0,
  },
};

const CartPage: FC = () => {
  const classes = useStyles();
  const {t} = useTranslate();
  const dispatch = useDispatch();
  const {checkoutProgress} = useSelector(selectCartStepperStatus);
  const localStorageKey = useCartLocalStorageKey();
  const [products, setProducts] = useState<FullProduct[]>([]);
  const quickOrderId = useQuickOrderId();

  const [amounts, setAmounts] = useState<AmountsState>(initialAmountsState);
  const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType>(
    PaymentMethodType.cardPayment
  );

  const showId = useCurrentShowId();
  const exhibitorPairId = useCurrentExhibitorPairId();
  const [show] = useCurrentShow();

  const [exhibitorPair] = useCurrentExhibitorPair({
    showId,
    exhibitorPairId,
  });

  const isIdealPaymentFailed = useResumeFailedIdealPayment({
    hasProducts: !!products.length,
    exhibitorPair,
    paymentMethodType,
    setPaymentMethodType,
  });

  const [neededProductIds, setNeededProductIds] = useState<string[]>([]);
  const [
    isBankTransferAllowedByQuickOrder,
    setIsBankTransferAllowedByQuickOrder,
  ] = useState(false);

  const {
    products: showProducts,
    organiserCompany,
    isReady: isReadyShowProducts,
    isResolved: isResolvedShowProducts,
    forgettableProductIds,
    fetchProducts,
  } = useShowProductsByIds(showId, neededProductIds);

  const fetchProductsRef = useRef(fetchProducts);
  const isSurchargeProductsRefreshInProgress = useRef(false);
  const cartProductIdsRef = useRef<string[]>([]);
  const cartSupplierCompanyIdsRef = useRef<string[]>([]);
  const appliedDiscountCodes = useShowDiscountCodes(showId);
  const {
    validate: validateDiscountCodes,
    autoCatch: autoCatchDiscountCode,
  } = useShowDiscountCodesActions();
  const {
    fetchManyProductPurchasesPerStand,
  } = useProductPurchasesPerStandActions();
  const {categoryCodesBySupplier} = useCategoryCodesState();

  const salesTaxPercentage = show?.salesTaxPercentage || 0;
  const MIN_BANK_TRANSFER_INVOICE_AMOUNT = useMemo(() => {
    if (!show) {
      return 0;
    }
    return MIN_AMOUNT_BY_CURRENCY[show.currency];
  }, [show]);
  const categoryIdsBySupplier = useMemo(
    () => getUniqueProductCategoryIdsBySupplier(products),
    [products]
  );

  const purchasableShowProducts = useMemo(
    () => (showProducts ?? []).filter(({isVisible}) => isVisible !== false),
    [showProducts]
  );

  useEffect(() => {
    let isCancelled = false;

    const resolveNeededProductIds = async () => {
      const cartItems = getItem<LocalStorageProduct[]>(localStorageKey, []);
      // Products carried a Firebase-style id before the move to Postgres
      // uuid primary keys. A cart item added back then and never checked
      // out still has the old id, which the API can no longer resolve, so
      // purge it here instead of resending it forever.
      const validCartItems = (cartItems || []).filter((item) =>
        isValidUuid(item.id)
      );
      if (validCartItems.length !== (cartItems || []).length) {
        writeItem(localStorageKey, validCartItems);
      }
      const cartIds = validCartItems.map((item) => item.id);

      if (!quickOrderId || !showId) {
        if (!isCancelled) {
          setNeededProductIds(cartIds);
          setIsBankTransferAllowedByQuickOrder(false);
        }
        return;
      }

      try {
        const quickOrder = await orderRepository.getItem(showId, quickOrderId);
        // Same legacy-id risk as the cart above, but this is a stored order
        // document, not a local cache — filter rather than rewrite it.
        const quickOrderIds = (
          quickOrder?.products.map((product) => product.id) ?? []
        ).filter(isValidUuid);
        if (!isCancelled) {
          setNeededProductIds(
            Array.from(new Set([...cartIds, ...quickOrderIds]))
          );
          setIsBankTransferAllowedByQuickOrder(
            isBankTransferAllowedByOrder(quickOrder)
          );
        }
      } catch (error) {
        console.error("Error resolving quick order product ids: ", error);
        if (!isCancelled) setNeededProductIds(cartIds);
      }
    };

    resolveNeededProductIds();

    return () => {
      isCancelled = true;
    };
  }, [localStorageKey, quickOrderId, showId]);

  useEffect(() => {
    const unsubscribe = autoCatchDiscountCode(showId);
    return unsubscribe;
  }, [showId]);

  useEffect(() => {
    if (!exhibitorPair || !showId) return;

    fetchManyProductPurchasesPerStand({showId, standId: exhibitorPair.standId});
  }, [exhibitorPair, showId]);

  useEffect(() => {
    fetchProductsRef.current = fetchProducts;
  }, [fetchProducts]);

  useEffect(() => {
    fetchProductsRef.current = fetchProducts;
  }, [fetchProducts]);

  useEffect(() => {
    cartProductIdsRef.current = products.map(({id}) => id);
    cartSupplierCompanyIdsRef.current = Array.from(
      new Set(products.map((product) => product.owner?.id).filter(Boolean))
    ) as string[];
  }, [products]);

  useEffect(() => {
    if (!showId) {
      return;
    }

    const listenerIdByShow = `${CommonNotificationType.UPDATE_SURCHARGE}_EXHIBITOR_CART_SHOW_${showId}`;
    const listenerIdCommon = `${CommonNotificationType.UPDATE_SURCHARGE}_EXHIBITOR_CART_COMMON_${showId}`;
    const handleSurchargeUpdate = async (
      notification:
        | INotificationBaseRelatedToShow<IPayloadUpdatedSurcharge>
        | INotificationBaseMessage<IPayloadUpdatedSurcharge>
    ) => {
      const {payload} = notification;
      if (!payload) {
        return;
      }

      const payloadShowId =
        payload.showId ||
        ("showId" in notification ? notification.showId : undefined);

      if (payloadShowId && payloadShowId !== showId) {
        return;
      }

      const payloadProductIds = payload.productIds || [];
      const hasMatchingProduct = payloadProductIds.some((id) =>
        cartProductIdsRef.current.includes(id)
      );
      const hasMatchingSupplier =
        !!payload.supplierCompanyId &&
        cartSupplierCompanyIdsRef.current.includes(payload.supplierCompanyId);

      if (!payloadShowId && !hasMatchingProduct && !hasMatchingSupplier) {
        return;
      }

      if (
        payloadProductIds.length &&
        !hasMatchingProduct &&
        !hasMatchingSupplier
      ) {
        return;
      }

      if (isSurchargeProductsRefreshInProgress.current) {
        return;
      }

      isSurchargeProductsRefreshInProgress.current = true;
      try {
        await fetchProductsRef.current();
      } catch (error) {
        console.error("Error during surcharge update in cart: ", error);
      } finally {
        isSurchargeProductsRefreshInProgress.current = false;
      }
    };

    apiV2.notification.subscribe<
      INotificationBaseRelatedToShow<IPayloadUpdatedSurcharge>
    >({
      key: CommonNotificationType.UPDATE_SURCHARGE,
      showId,
      listenerId: listenerIdByShow,
      callback: handleSurchargeUpdate,
    });

    apiV2.notification.subscribe<
      INotificationBaseMessage<IPayloadUpdatedSurcharge>
    >({
      key: CommonNotificationType.UPDATE_SURCHARGE,
      listenerId: listenerIdCommon,
      callback: handleSurchargeUpdate,
    });

    IdleManager.getInstance().addSubscriber(
      `surcharges-${showId}`,
      async () => {
        await fetchProductsRef?.current?.();
      }
    );

    return () => {
      apiV2.notification.unsubscribe({
        key: CommonNotificationType.UPDATE_SURCHARGE,
        showId,
        listenerId: listenerIdByShow,
      });
      apiV2.notification.unsubscribe({
        key: CommonNotificationType.UPDATE_SURCHARGE,
        listenerId: listenerIdCommon,
      });
    };
  }, [showId]);

  const linkedPropertyByProductId = useMemo(
    () =>
      getLinkedPropertyByProductId(products, show?.unitSystem, exhibitorPair),
    [products, show?.unitSystem, exhibitorPair]
  );

  const shoppingCartRef = useRef<HTMLDivElement | null>(null);

  const onExtraProductChange = (
    id: string,
    parentProductId: string,
    {cartQuantity: quantity}: ChangingProductFields
  ) => {
    const updatedProducts = products.map((product) => {
      if (product.id === parentProductId) {
        const extras = product.extras?.map((extraProduct) => {
          return extraProduct.id === id
            ? {
                ...extraProduct,
                quantity: quantity || extraProduct.quantity,
              }
            : extraProduct;
        });
        return {...product, extras};
      }

      return product;
    }) as FullProduct[];

    setProducts(updatedProducts);
    const localStorageProducts = updatedProducts.map(
      mapProductToLocalStorageProduct
    );

    writeItem(localStorageKey, localStorageProducts);
  };

  const onProductChange = (
    id: string,
    {cartQuantity: newCartQuantity, notes}: ChangingProductFields
  ) => {
    const localStorageProducts = getItem<LocalStorageProduct[]>(
      localStorageKey
    );
    const updatedProducts = products.map((product) => {
      if (product.id !== id) {
        return product;
      }
      const storageExtras = localStorageProducts.find((p) => p.id === id)
        ?.extras;
      const newExtras = (product.extras ?? []).reduce<
        LocalStorageExtraProduct[]
      >((acc, extraProduct) => {
        const storageExtra = storageExtras?.find(
          (e) => e.id === extraProduct.id
        );
        if (!storageExtra) return acc;
        acc.push({
          ...storageExtra,
          ...(extraProduct.isSelectedByDefault && {quantity: newCartQuantity}),
        });
        return acc;
      }, []);

      return {
        ...product,
        cartQuantity: newCartQuantity ?? product.cartQuantity,
        ...(notes &&
          Object.keys(notes).includes("note") && {
            notes: {
              ...product.notes,
              note: notes?.note,
            },
          }),
        ...(newExtras.length && {extras: newExtras}),
        ...(notes &&
          Object.keys(notes).includes("attachments") && {
            notes: {
              ...product.notes,
              attachments: notes?.attachments,
            },
          }),
      };
    }) as FullProduct[];

    setProducts(updatedProducts);
    writeItem(
      localStorageKey,
      updatedProducts.map(mapProductToLocalStorageProduct)
    );
  };

  const resetCartStatus = useCallback(() => {
    dispatch(setShowCartCount(0));
    dispatch(goToCart());
    dispatch(updateTotalAmount({withoutApproval: 0, withApproval: null}));
  }, [dispatch]);

  const mergeProducts = useCallback(async () => {
    if (!isResolvedShowProducts) {
      return;
    }

    if (!isReadyShowProducts) {
      return;
    }

    const rawCartItems = getItem<LocalStorageProduct[]>(localStorageKey, []);
    const isWholeCartForgettable =
      !!rawCartItems.length &&
      rawCartItems.every(({id}) => forgettableProductIds.has(id));

    if (
      !purchasableShowProducts.length &&
      isWholeCartForgettable &&
      !quickOrderId
    ) {
      resetCartStatus();
      setProducts([]);
      writeItem(localStorageKey, []);
      return;
    }

    if (quickOrderId) {
      if (!show || !isReadyShowProducts) {
        return;
      }
      const quickOrder = await orderRepository.getItem(showId, quickOrderId);
      if (!quickOrder) {
        throw new Error("Quick order not found");
      }
      const isAnyDifferenceBetweenQuickOrderAndShowProducts =
        quickOrder.products.length !== products.length ||
        products.some(
          (product) =>
            !quickOrder.products.find(
              (quickOrderProduct) => quickOrderProduct.id === product.id
            )
        );
      if (!isAnyDifferenceBetweenQuickOrderAndShowProducts) {
        if (!organiserCompany) {
          return;
        }

        const quickOrderProductsWithDiscounts = applyDiscountCodesToProducts(
          products,
          appliedDiscountCodes,
          organiserCompany
        );
        if (!isEqual(products, quickOrderProductsWithDiscounts)) {
          setProducts(quickOrderProductsWithDiscounts);
        }
        return;
      }

      const supplierCompanyId = quickOrder.products[0].owner.id;

      const quickOrderProducts = await apiV2.product.getByIdsAndSupplier({
        ids: quickOrder.products.map((product) => product.id).join(","),
        supplierCompanyId,
      });

      const quickOrderOrganiserCompany = await apiV2.organiserCompany.getOneByShowId(
        {showId}
      );

      if (!quickOrderOrganiserCompany) {
        throw new Error("Organiser company not found");
      }

      const [
        supplierCompanies,
        supplierCompanyShowManagers,
        supplierCompanyStripeAccounts,
      ] = await Promise.all([
        apiV2.supplierCompany.getAllByIds({
          supplierCompanyIds: supplierCompanyId,
          withProductCategories: true,
        }),
        apiV2.supplierCompany.getShowManagersBySupplierCompanyIds({
          showId,
          organiserCompanyId: quickOrderOrganiserCompany.id,
          supplierCompanyIds: supplierCompanyId,
          withSupplierCompanyShowProductCategories: true,
        }),
        Promise.all(
          [supplierCompanyId].map(async (id) => {
            const stripeAccount = await apiV2.oldStripe.getSupplierCompanyStripeConnectAccount(
              {
                supplierCompanyId: id,
              }
            );
            return {...stripeAccount, supplierCompanyId: id};
          })
        ),
      ]);

      const supplierCompanyMap = new Map(
        supplierCompanies.map((company) => [company.id, company])
      );
      const showManagerMap = new Map(
        supplierCompanyShowManagers
          .filter(Boolean)
          .map((manager) => [
            manager?.organiserCompanyToSupplierCompany?.supplierCompanyId,
            manager,
          ])
      );
      const stripeAccountMap = new Map(
        supplierCompanyStripeAccounts
          .filter((acc) => acc?.accountId)
          .map((acc) => [acc.supplierCompanyId, acc])
      );

      const showSupplierCompanies = await apiV2.supplierCompany.getAllByShowId({
        showId,
      });

      const fullProducts: FullProduct[] = quickOrder.products
        .map((quickOrderProduct) => {
          const showProduct = showProducts?.find(
            (showProduct) => showProduct.id === quickOrderProduct.id
          );
          const configs: ShowProductConfig[] =
            quickOrderProduct.type === QuickOrderProductType.SELECTED
              ? showProduct?.configs || []
              : [
                  ({
                    id: quickOrderProduct.cpoConfig?.id || "",
                    prices: [
                      {
                        id: quickOrderProduct.cpoConfig?.priceId || "",
                      } as ICustomProductOptionPrice,
                    ],
                    items: [],
                  } as unknown) as ShowProductConfig,
                ];
          const product = quickOrderProducts.find(
            (orderProduct) => orderProduct.id === quickOrderProduct.id
          );
          if (!product) {
            throw new Error("Product not found");
          }

          const categories = showProduct?.categories?.length
            ? showProduct.categories
            : product.categories || [];

          const productConfig = configs.find(
            ({id}) => id === quickOrderProduct?.cpoConfig?.id
          );

          const {approval, ...productData} = product;

          const owner = supplierCompanyMap.get(supplierCompanyId);
          const supplierCompanyShowManager = showManagerMap.get(
            supplierCompanyId
          );
          const supplierCompanyStripeAccount = stripeAccountMap.get(
            supplierCompanyId
          );

          if (
            !owner ||
            !supplierCompanyShowManager ||
            !supplierCompanyStripeAccount
          ) {
            return null;
          }

          const showSupplierCompanyData = showSupplierCompanies.find(
            (supplierCompany) => supplierCompany.id === supplierCompanyId
          );

          if (!showSupplierCompanyData) {
            return null;
          }

          const productOwner = {
            ...(owner || {}),
            ...(showSupplierCompanyData || {}),
            id: supplierCompanyId,
          };

          const selectedProductConfig = configs?.find(
            (config) => config.id === quickOrderProduct?.cpoConfig?.id
          );

          const basePrice =
            selectedProductConfig?.price ?? quickOrderProduct.price ?? 0;

          const platformFeeAmount =
            quickOrderOrganiserCompany?.fee && basePrice
              ? calculatePlatformFeeAmount(
                  basePrice,
                  quickOrderOrganiserCompany.fee
                )
              : 0;

          const paymentFeeAmount =
            quickOrderOrganiserCompany?.paymentFee && basePrice
              ? calculatePaymentFeeAmount(
                  basePrice,
                  quickOrderOrganiserCompany.paymentFee
                )
              : 0;

          const priceWithFees = calculateProductPriceWithFees(
            basePrice,
            quickOrderOrganiserCompany.fee,
            supplierCompanyShowManager.organiserFee ?? 0
          );
          const extras = quickOrderProduct?.extras?.length
            ? quickOrderProduct.extras
            : [];
          const supplierCategoryCodes = categoryCodesBySupplier.get(
            showSupplierCompanyData.id
          );

          const projectCode = supplierCategoryCodes
            ? getMergedProjectCode({
                categories,
                show,
                supplierCategoryCodes,
                supplierCompany: showSupplierCompanyData,
              })
            : null;

          const productConfigs: ICustomProductOptionConfig[] =
            product.customProductOptionConfigs ?? [];

          const defaultConfig = productConfigs[0];

          const productQuantity: number = defaultConfig?.quantity ?? 0;

          return {
            ...productData,
            price: basePrice,
            basePrice: showProduct?.basePrice,
            showPrice: showProduct?.showPrice,
            paymentFeeAmount,
            platformFeeAmount,
            imageId: quickOrderProduct.image?.id,
            categories,
            priceWithFees,
            cartQuantity: quickOrderProduct?.quantity || productQuantity,
            quantity: productConfig
              ? (productConfig.showQuantity ??
                  productConfig.quantity ??
                  productQuantity) - (productConfig.purchasedQuantity || 0)
              : productQuantity,
            invoiceId: quickOrderProduct.invoiceId,
            configs,
            approval: false,
            cpoConfigId: quickOrderProduct?.cpoConfig?.id,
            fees: showProduct?.fees || [],
            extras,
            organiserFee: quickOrderOrganiserCompany.fee,
            ownerId: supplierCompanyId,
            owner: productOwner,
            ownerToShow: supplierCompanyShowManager,
            ...(projectCode && {projectCode}),
          };
        })
        .filter(Boolean) as FullProduct[];

      const quickOrderProductsWithDiscounts = applyDiscountCodesToProducts(
        fullProducts,
        appliedDiscountCodes,
        quickOrderOrganiserCompany
      );
      if (!isEqual(products, quickOrderProductsWithDiscounts)) {
        setProducts(quickOrderProductsWithDiscounts);
      }
      return;
    }

    if (organiserCompany && show && purchasableShowProducts.length) {
      const mergedProducts = mergeProductsHelper(
        purchasableShowProducts,
        appliedDiscountCodes,
        (newProducts: LocalStorageProduct[]) =>
          dispatch(setShowCartCount(getProductsTotalQuantity(newProducts))),
        organiserCompany,
        localStorageKey,
        (productId) => forgettableProductIds.has(productId)
      );

      if (!isEqual(products, mergedProducts)) {
        setProducts(mergedProducts);
      }
    }
  }, [
    isReadyShowProducts,
    isResolvedShowProducts,
    organiserCompany,
    show,
    showProducts,
    purchasableShowProducts,
    forgettableProductIds,
    products,
    resetCartStatus,
    appliedDiscountCodes,
    categoryCodesBySupplier,
  ]);

  useEffect(() => {
    mergeProducts();
  }, [mergeProducts]);

  const {productsWithApproval, productsWithoutApproval} = useMemo(
    () => separateProductsWithApproval(products),
    [products]
  );

  // NOTE: Price calculation
  useEffect(() => {
    if (!products.length) {
      resetCartStatus();
    }
    if (products) {
      const approvalNotRequiredAmounts = getAmounts({
        products: productsWithoutApproval,
        paymentMethodType,
        salesTaxPercentage,
        groupByProperty: "owner.id",
      });
      const approvalRequiredAmounts = getAmounts({
        products: productsWithApproval,
        paymentMethodType,
        salesTaxPercentage,
        groupByProperty: "owner.id",
      });

      const approvalProductsBySupplier = groupBy(
        productsWithApproval,
        "owner.id"
      );
      const withApprovalTotalAmounts = Object.entries(
        approvalProductsBySupplier
      ).reduce((acc, [supplierId, products]) => {
        const amounts = getAmounts({
          products,
          paymentMethodType,
          salesTaxPercentage,
          groupByProperty: "approval.supplier.id",
        });
        return {
          ...acc,
          [supplierId]: getFinalTotalAmount(
            amounts,
            MIN_BANK_TRANSFER_INVOICE_AMOUNT
          ),
        };
      }, {} as CartWithApprovalAmount);

      setAmounts({
        approvalNotRequired: approvalNotRequiredAmounts,
        approvalIsRequired: approvalRequiredAmounts,
      });
      dispatch(
        updateTotalAmount({
          withoutApproval: getFinalTotalAmount(
            approvalNotRequiredAmounts,
            MIN_BANK_TRANSFER_INVOICE_AMOUNT
          ),
          withApproval: withApprovalTotalAmounts,
        })
      );
    }

    const productsItems = getItem<LocalStorageProduct[]>(localStorageKey, []);
    if (productsItems?.length) {
      const productsTotalQuantity = getProductsTotalQuantity(productsItems);
      dispatch(setShowCartCount(productsTotalQuantity));
    }
  }, [
    dispatch,
    products,
    resetCartStatus,
    paymentMethodType,
    salesTaxPercentage,
    MIN_BANK_TRANSFER_INVOICE_AMOUNT,
  ]);

  if (!products || !show) {
    return null;
  }

  const resetScrollIfTooTall = () => {
    const shoppingCartEl = shoppingCartRef.current;
    if (!shoppingCartEl) {
      return;
    }

    const shoppingCartHeight = shoppingCartEl.scrollHeight;
    if (shoppingCartHeight > window.innerHeight) {
      shoppingCartEl.scrollIntoView({block: "start", behavior: "smooth"});
    }
  };
  const onRemove = async (id: string) => {
    const updatedProducts = products.filter((product) => product.id !== id);
    setProducts(updatedProducts);

    writeItem(
      localStorageKey,
      updatedProducts.map(mapProductToLocalStorageProduct)
    );
    if (products?.length === 1) {
      setAmounts(initialAmountsState);
      dispatch(setShowCartCount(0));
    }
    const product = products.find((product) => product.id === id);
    if (product) {
      await updateNotesAttachments({
        productId: id,
        newAttachments: [],
        oldAttachments: product.notes?.attachments || [],
      });
    }
  };

  const onRemoveExtraProduct = async (id: string, parentProductId: string) => {
    const deleteExtraProduct = (product: FullProduct) => {
      if (product.id === parentProductId) {
        const updatedExtras = product.extras?.filter(
          (extraProduct: ExtraProduct) => extraProduct.id !== id
        );
        return {...product, extras: updatedExtras};
      }
      return product;
    };
    const updatedProducts = products.map(deleteExtraProduct);
    setProducts(updatedProducts);

    writeItem(
      localStorageKey,
      updatedProducts.map(mapProductToLocalStorageProduct)
    );
  };

  const changeQuantity = (id: string, cartQuantity: number) => {
    onProductChange(id, {cartQuantity: roundCartQuantity(cartQuantity)});
  };

  const changeExtraProductQuantity = (
    id: string,
    parentProductId: string,
    cartQuantity: number
  ) => {
    onExtraProductChange(id, parentProductId, {
      cartQuantity: roundCartQuantity(cartQuantity),
    });
  };

  const stepperCart = () => {
    const createProgressSteps = (progress: CartProgress) => [
      {
        icon: (
          <StepperImageContainer completed={!progress} icon={<ReviewIcon />} />
        ),
        label: t("exhibitor.cart.stepper.review_products"),
      },
      {
        icon: (
          <StepperImageContainer
            completed={!!progress}
            icon={<PaymentIcon style={{color: "white"}} />}
          />
        ),
        label: t("exhibitor.cart.stepper.payment"),
      },
    ];

    const onCheckout = async (notes: RawNotes) => {
      if (!exhibitorPair) {
        return;
      }
      const isDiscountCodesValid = await validateDiscountCodes(
        showId,
        exhibitorPair.standId,
        categoryIdsBySupplier
      );
      if (!isDiscountCodesValid) {
        return;
      }
      const updatedProducts = products.map((product) => {
        const emptyNotes = {note: "", attachments: []};

        return {
          ...product,
          notes: notes[product.id] || emptyNotes,
        };
      });

      setProducts(updatedProducts);
      writeItem(
        localStorageKey,
        updatedProducts.map(mapProductToLocalStorageProduct)
      );

      resetScrollIfTooTall();
      dispatch(goToPayment());
    };

    const onNoteChange = (id: string, note: string) => {
      onProductChange(id, {notes: {note}});
    };
    const onProductAttachmentsChange = (
      id: string,
      attachments: ProductNoteAttachment[]
    ) => onProductChange(id, {notes: {attachments}});

    const renderStepContent = () => {
      switch (checkoutProgress) {
        case CartProgress.PAYMENT:
          return (
            <Checkout
              showProducts={products}
              show={show}
              paymentMethodType={paymentMethodType}
              changePaymentMethodType={setPaymentMethodType}
              categoryIdsBySupplier={categoryIdsBySupplier}
              isBankTransferAllowedByOrder={isBankTransferAllowedByQuickOrder}
              isIdealPaymentFailed={isIdealPaymentFailed}
            />
          );
        default:
          return (
            <div className={classes.cart} ref={shoppingCartRef}>
              <ShoppingCart
                productsWithoutApproval={productsWithoutApproval}
                productsWithApproval={productsWithApproval}
                onCheckout={onCheckout}
                onProductAttachmentsChange={onProductAttachmentsChange}
                onNoteChange={onNoteChange}
                onRemoveProduct={onRemove}
                onRemoveExtraProduct={onRemoveExtraProduct}
                onChangeQuantity={changeQuantity}
                onChangeExtraProductQuantity={changeExtraProductQuantity}
                linkedPropertyByProductId={linkedPropertyByProductId}
              />
            </div>
          );
      }
    };

    return (
      <div
        className={classes.container}
        {...generateTestId("exhibitor-cart-products-container")}
      >
        <Stepper
          activeStep={checkoutProgress}
          steps={createProgressSteps(checkoutProgress)}
          nonLinear
          stepperClassName={classes.horizontalStepper}
        />
        {renderStepContent()}
      </div>
    );
  };

  return (
    <ShoppingSummaryContextProvider>
      <Box className={classes.root}>
        {stepperCart()}
        <FlexBox
          className={classes.summary}
          {...generateTestId("exhibitor-cart-summary-container")}
        >
          <ShoppingSummary
            totalAmount={
              amounts.approvalIsRequired.totalAmount +
              amounts.approvalNotRequired.totalAmount
            }
            subtotalAmount={
              amounts.approvalIsRequired.subtotalAmount +
              amounts.approvalNotRequired.subtotalAmount
            }
            taxAmount={
              amounts.approvalIsRequired.taxAmount +
              amounts.approvalNotRequired.taxAmount
            }
            paymentFeeAmount={
              amounts.approvalIsRequired.paymentFeeAmount +
              amounts.approvalNotRequired.paymentFeeAmount
            }
            discountAmount={
              amounts.approvalIsRequired.discountAmount +
              amounts.approvalNotRequired.discountAmount
            }
            productsWithApproval={productsWithApproval}
            productsWithoutApproval={productsWithoutApproval}
            show={show}
            paymentMethodType={paymentMethodType}
          />
          {exhibitorPair?.stand && showProducts && (
            <CartDiscountCodes
              products={products}
              stand={exhibitorPair.stand}
              categoryIdsBySupplier={categoryIdsBySupplier}
              showProducts={showProducts}
            />
          )}
        </FlexBox>
      </Box>
    </ShoppingSummaryContextProvider>
  );
};

export default CartPage;
